import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  ResponsiveContent,
  ResponsiveHeader,
  ResponsiveFooter,
} from '../ui/ResponsiveLayout';

const Frame174: React.FC = () => {
  const navigate = useNavigate();

  const handleNext = () => {
    navigate('/frame-186');
  };

  const handleBack = () => {
    navigate('/frame-140');
  };

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        <ResponsiveHeader title="Inventory Your Financial Assets and Accounts" />

        <div className="max-w-4xl space-y-8">
          {/* Introduction */}
          <div className="space-y-4">
            <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
              Taking time to document your assets helps ensure clarity, continuity, and care for
              the people who may one day need to manage them. This process gives your loved ones a
              clearer picture of what you own, where it lives, and how it should be handled,
              reducing confusion during an already difficult time.
            </p>
            <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
              You don’t need to have everything perfectly organized to begin. You can move at your
              own pace and update details whenever you’re ready.
            </p>
          </div>

          {/* What You'll Do */}
          <div className="bg-white rounded-lg border border-neutral-200 p-6">
            <h2 className="text-xl font-semibold text-cyan-900 mb-4">What You’ll Do</h2>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-700 text-white flex items-center justify-center font-semibold">
                  1
                </div>
                <div>
                  <p className="text-stone-700 font-bold">
                    Record your financial accounts, investments, and retirement assets
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-700 text-white flex items-center justify-center font-semibold">
                  2
                </div>
                <div>
                  <p className="text-stone-700 font-bold">
                    Document properties, business interests, and other valuable holdings
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-700 text-white flex items-center justify-center font-semibold">
                  3
                </div>
                <div>
                  <p className="text-stone-700 font-bold">
                    Upload and organize tax documents and records (federal/state returns, property
                    tax records, and other tax docs)
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-700 text-white flex items-center justify-center font-semibold">
                  4
                </div>
                <div>
                  <p className="text-stone-700 font-bold">
                    Add supporting documents, notes, and special instructions
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-700 text-white flex items-center justify-center font-semibold">
                  5
                </div>
                <div>
                  <p className="text-stone-700 font-bold">
                    Review a summarized view of your assets in one secure place
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Why This Matters */}
          <div className="bg-cyan-50 border border-cyan-100 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-cyan-900 mb-3">Why This Matters</h2>
            <ul className="space-y-2 text-stone-700">
              <li className="flex items-start gap-2">
                <span className="text-cyan-700 mt-1">✓</span>
                <span>
                  Gives your loved ones a clear roadmap of your financial life including tax
                  history and supporting records
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-700 mt-1">✓</span>
                <span>Reduces stress, delays, and guesswork during estate settlement</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-700 mt-1">✓</span>
                <span>Helps prevent overlooked accounts, assets, or documents</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-700 mt-1">✓</span>
                <span>Makes it easier to work with an attorney, CPA, or executor if needed</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-700 mt-1">✓</span>
                <span>Keeps important information securely organized and accessible</span>
              </li>
            </ul>
          </div>

          {/* Estimated Time */}
          <div className="text-center text-stone-600">
            <p className="text-sm">
              <span className="font-semibold">Estimated time:</span> Approximately 60–120 minutes
            </p>
            <p className="text-xs mt-1">You can save progress and return at any time.</p>
          </div>
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Inventory Your Assets"
        subtitle="Start documenting your assets"
        progressPercent={0}
        onNext={handleNext}
        onBack={handleBack}
        nextText="Get Started"
        backText="Back to Dashboard"
      />
    </ResponsiveContainer>
  );
};

export default Frame174;